import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    alpha,
    Box,
    ButtonBase,
    Grid,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import AutoFixHighRounded from "@mui/icons-material/AutoFixHighRounded";
import WbSunnyRounded from "@mui/icons-material/WbSunnyRounded";
import Booking from "../../../api/Booking";
import Location, { DayCountSummary } from "../../../api/Location";
import Schedule from "../../../api/Schedule";
import { useCookies } from "react-cookie";
import { DayView } from "./MonthView";
import { parseWithOffset } from "../../../utils/dayjsUtils";
import FreeSlotItem, { FreeBookingSlot } from "./FreeSlotItem";
import BlockedSlotItem, { BlockedTimeSlot } from "./BlockedSlotItem";
import AlertModal from "../../Modals/AlertModal";
import {
    dayOffModalContent,
    fillDayModalContent,
} from "../../../utils/modalsContent";
import BookingItem from "./BookingItem";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorDialog from "../../ErrorDialog";
import AIAdmin from "../../../api/AIAdmin";
import { FONTS } from "../../../constants/styles";

dayjs.extend(utc);

interface Item {
    value: Booking | BlockedTimeSlot | FreeBookingSlot;
    type: "book" | "blocked" | "free";
}

interface Weekday {
    day: dayjs.Dayjs;
    summary: DayCountSummary | undefined;
}

interface Props {
    selectedDay: dayjs.Dayjs;
    setSelectedDay: (val: dayjs.Dayjs) => void;
    onSwitchToMonth: () => void;
    onScroll: (y: number) => void;
}

const WeekView = (props: Props) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { selectedDay, setSelectedDay, onSwitchToMonth, onScroll } = props;
    const hex = theme.palette.primary.main;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [week, setWeek] = useState<Weekday[]>([]);
    const [bookings, setBookings] = useState<Item[]>([]);
    const [outdatedSchedule, setOutdatedSchedule] = useState(true);

    const [dayOffModalOpen, setDayOffModalOpen] = useState(false);
    const [fillDayModalOpen, setFillDayModalOpen] = useState(false);
    const [jobExistsModalOpen, setJobExistsModalOpen] = useState(false);
    const [bookingDisabledModalOpen, setBookingDisabledModalOpen] =
        useState(false);

    // To prevent fetching schedule for default selectedDay (today) in case there is one in session storage
    const [selectedDayHydrated, setSelectedDayHydrated] = useState(false);

    // Logic for showing modals after redirects
    const location = useLocation();
    const state = location.state as {
        freeMyDaySuccess?: boolean;
        fillMyDaySuccess?: boolean;
        error?: string;
    };
    const [freedModalOpen, setFreedModalOpen] = useState(false);
    const [filledModalOpen, setFilledModalOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Recurring blocked slots visual feedback
    const [makeRecurringSuccessOpen, setMakeRecurringSuccessOpen] =
        useState(false);
    const [makeRecurringSuccessCount, setMakeRecurringSuccessCount] =
        useState(0);
    const [makeRecurringConflictCount, setMakeRecurringConflictCount] =
        useState(0);
    const [makeRecurringError, setMakeRecurringError] = useState<string | null>(
        null
    );

    // Today
    const today = dayjs();

    // Scroll ref
    const scrollRef = useRef<HTMLDivElement>(null);
    const handleScroll = useCallback(() => {
        if (scrollRef.current) onScroll(scrollRef.current.scrollTop);
    }, [onScroll]);

    // Logic for showing modals after redirects
    useEffect(() => {
        if (state?.freeMyDaySuccess) {
            setFreedModalOpen(true);
            navigate(".", { replace: true, state: {} });
        } else if (state?.fillMyDaySuccess) {
            setFilledModalOpen(true);
            navigate(".", { replace: true, state: {} });
        } else if (state?.error) {
            setErrorMessage(state.error);
            setErrorDialogOpen(true);
            navigate(".", { replace: true, state: {} });
        }
    }, [state, navigate]);

    useEffect(() => {
        const storedDate = sessionStorage.getItem("calendarSelectedDay");
        if (storedDate) {
            setSelectedDay(dayjs(storedDate));
        }
        setSelectedDayHydrated(true);
    }, [setSelectedDay]);

    useEffect(() => {
        if (!uuid || !brand_id || !location_id) return;

        let day = selectedDay;
        const week: Weekday[] = [];

        while (day.get("day") !== 1) {
            day = day.subtract(1, "day");
        }
        Location.bookingsWeek(
            uuid,
            brand_id,
            location_id,
            day.get("year"),
            day.get("month") + 1,
            day.get("date")
        )
            .then((weekSummaries: DayCountSummary[]) => {
                for (const summary of weekSummaries) {
                    week.push({ day, summary });
                    day = day.add(1, "day");
                }
                setWeek(week);
            })
            .catch(console.error);
    }, [uuid, brand_id, location_id, selectedDay]);

    useEffect(() => {
        if (!selectedDayHydrated) return;

        const fetchSchedule = async () => {
            try {
                const res = await Schedule.scheduleDay(
                    uuid,
                    brand_id,
                    location_id,
                    selectedDay.year(),
                    selectedDay.month() + 1,
                    selectedDay.date()
                );

                const slots = res?.slots;

                const combinedSlots: Item[] = (slots || []).map(
                    (slot: any) => ({
                        value: {
                            ...slot,
                            key: slot.key,
                            start: parseWithOffset(slot.start),
                            end: parseWithOffset(slot.end),
                            ...(slot.type === "free"
                                ? { working: slot.working }
                                : {}),
                        },
                        type: slot.type,
                    })
                );
                setBookings(combinedSlots);
                setOutdatedSchedule(false);
            } catch (error) {
                console.error("Error fetching schedule:", error);
            }
        };
        if (outdatedSchedule) fetchSchedule();
    }, [
        selectedDay,
        uuid,
        brand_id,
        location_id,
        outdatedSchedule,
        selectedDayHydrated,
    ]);

    // Helper to compose unique React keys for slots
    const slotReactKey = (slot: Item) => {
        const v: any = slot.value;
        const startIso = v.start?.toISOString?.() ?? String(v.start);
        const endIso = v.end?.toISOString?.() ?? String(v.end);

        switch (slot.type) {
            case "book":
                return `book-${
                    v.key ?? v.id ?? startIso
                }-${startIso}-${endIso}`;
            case "blocked":
                return `blocked-${v.key ?? startIso}-${startIso}-${endIso}`;
            case "free":
                return `free-${startIso}-${endIso}-${v.working ? "w" : "nw"}`;
            default:
                return `slot-${startIso}-${endIso}`;
        }
    };

    const handleChangeDay = useCallback(
        (weekday: Weekday) => {
            setSelectedDay(weekday.day);
            setOutdatedSchedule(true);
            sessionStorage.setItem(
                "calendarSelectedDay",
                weekday.day.toISOString()
            );
        },
        [setSelectedDay]
    );

    const handleBlockSlot = async (
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        note?: string
    ) => {
        try {
            await Booking.blockSlot(
                uuid,
                brand_id,
                location_id,
                start.toDate(),
                end.toDate(),
                note
            );
            // Refresh bookings list
            setOutdatedSchedule(true);
        } catch (err) {
            console.error("Error blocking a slot: ", err);
        }
    };

    const handleUnblockSlot = async (
        slot: BlockedTimeSlot,
        mode: "single" | "all"
    ) => {
        try {
            const success = await Booking.deleteBlockedSlot(
                uuid,
                brand_id,
                location_id,
                slot.key,
                mode
            );
            if (success) {
                setOutdatedSchedule(true);
            } else {
                console.error("Failed to delete blocked slot.");
            }
        } catch (err) {
            console.error("Error unblocking a slot: ", err);
        }
    };

    const handleEditSlot = async (
        slot: BlockedTimeSlot,
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        note?: string
    ) => {
        try {
            const success = await Booking.updateBlockedSlot(
                uuid,
                brand_id,
                location_id,
                slot.key,
                start.toDate(),
                end.toDate(),
                note
            );
            if (success) {
                setOutdatedSchedule(true);
            } else {
                console.error("Failed to update blocked slot.");
            }
        } catch (err) {
            console.error("Error updating blocked slot: ", err);
        }
    };

    const handleMakeRecurring = async (
        slot: BlockedTimeSlot,
        interval: "weekly" | "monthly"
    ) => {
        try {
            const result = await Booking.makeRecurringFromSlot(
                uuid,
                brand_id,
                location_id,
                slot.key,
                interval
            );

            if (result?.success) {
                setMakeRecurringConflictCount(result.skipped || 0);
                setMakeRecurringSuccessCount(result.inserted || 0);
                setMakeRecurringSuccessOpen(true);
                setOutdatedSchedule(true);
            } else {
                setMakeRecurringError(
                    "Failed to make this blocked slot recurring."
                );
            }
        } catch (err) {
            console.error("Error making a blocked slot recurring:", err);
            setMakeRecurringError("Something went wrong. Please try again.");
        }
    };

    const handleFillMyDayClick = useCallback(async () => {
        const date = selectedDay.format("YYYY-MM-DD");

        const result = await AIAdmin.fillMyDayStatus(
            uuid,
            brand_id,
            location_id,
            date
        );

        if (!result.ok) {
            setErrorMessage(result.error);
            setErrorDialogOpen(true);
            return;
        }

        if (!result.bookingEnabled) {
            setBookingDisabledModalOpen(true);
            return;
        }

        if (result.jobExists) {
            setJobExistsModalOpen(true);
            return;
        }

        navigate(`/aiadmin/fill-my-day/config?date=${date}`);
    }, [uuid, brand_id, location_id, selectedDay, navigate]);

    return (
        <Stack
            className="force-dynamic-translation"
            ref={scrollRef}
            onScroll={handleScroll}
            direction="column"
            width="100%"
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "transparent" }}
        >
            {/* Hero — brand gradient */}
            <Stack
                width="100%"
                sx={{
                    pt: "72px",
                    pb: 5,
                    px: 2,
                    position: "relative",
                    boxSizing: "border-box",
                }}
            >
                {/* Date display — frosted glass */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.18)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: "16px",
                        px: 2,
                        py: 1.25,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="36px"
                        fontWeight={700}
                        color="white"
                        lineHeight={1}
                    >
                        {selectedDay.get("date")}
                    </Typography>
                    <Stack spacing={0}>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="15px"
                            fontWeight={600}
                            color="white"
                            lineHeight={1.3}
                        >
                            {selectedDay.format("MMMM")}
                        </Typography>
                        <Typography
                            fontSize="12px"
                            color="rgba(255,255,255,0.7)"
                            lineHeight={1.3}
                        >
                            {selectedDay.format("dddd")}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Weekday headers + day cells — frosted glass */}
                <Stack
                    sx={{
                        bgcolor: "rgba(255,255,255,0.18)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: "16px",
                        py: 1,
                        mt: 2,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                <Grid container columns={7} width="100%">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                        <Grid
                            item
                            xs={1}
                            key={d}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Typography
                                fontSize="12px"
                                fontWeight={600}
                                color="rgba(255,255,255,0.6)"
                            >
                                {d}
                            </Typography>
                        </Grid>
                    ))}
                    {week.map((x) => (
                        <Grid
                            item
                            xs={1}
                            key={x.day.toString()}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <DayView
                                day={x.day}
                                selected={
                                    x.day.format("DD.MM.YYYY") ===
                                    selectedDay?.format("DD.MM.YYYY")
                                }
                                current={
                                    x.day.format("DD.MM.YYYY") ===
                                    today.format("DD.MM.YYYY")
                                }
                                onClick={() => handleChangeDay(x)}
                                summary={x.summary}
                                onGradient
                            />
                        </Grid>
                    ))}
                </Grid>
                </Stack>
            </Stack>

            {/* Bottom sheet */}
            <Stack
                sx={{
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    position: "relative",
                    zIndex: 1,
                    pt: 3,
                    px: 2,
                    pb: "120px",
                    boxSizing: "border-box",
                    flex: 1,
                }}
            >
                {/* Toggle pills */}
                <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={1}
                    mb={2}
                >
                    <ButtonBase
                        sx={{
                            borderRadius: 20,
                            px: 2,
                            py: 0.75,
                            bgcolor: alpha(hex, 0.12),
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color={hex}
                        >
                            Week
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={onSwitchToMonth}
                        sx={{
                            borderRadius: 20,
                            px: 2,
                            py: 0.75,
                            bgcolor: "transparent",
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color="text.secondary"
                        >
                            Month
                        </Typography>
                    </ButtonBase>
                </Stack>

                {/* Action chips */}
                <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={1}
                    mb={2}
                >
                    <ButtonBase
                        onClick={() => setDayOffModalOpen(true)}
                        sx={{
                            borderRadius: 20,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: alpha(hex, 0.06),
                        }}
                    >
                        <EventBusyRounded
                            sx={{ fontSize: 16, color: hex, mr: 0.75 }}
                        />
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color={hex}
                        >
                            Day off
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => setFillDayModalOpen(true)}
                        sx={{
                            borderRadius: 20,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: alpha(hex, 0.06),
                        }}
                    >
                        <AutoFixHighRounded
                            sx={{ fontSize: 16, color: hex, mr: 0.75 }}
                        />
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color={hex}
                        >
                            Fill day
                        </Typography>
                    </ButtonBase>
                </Stack>

                {/* Slot list with stagger */}
                <Stack
                    spacing={0.5}
                    sx={{
                        "@keyframes fadeSlideUp": {
                            from: {
                                opacity: 0,
                                transform: "translateY(8px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    {bookings.map((slot, index) => (
                        <Box
                            key={slotReactKey(slot)}
                            sx={{
                                animation: "fadeSlideUp 0.25s ease-out both",
                                animationDelay: `${index * 30}ms`,
                            }}
                        >
                            {slot.type === "book" ? (
                                <BookingItem
                                    uuid={uuid}
                                    brand_id={brand_id}
                                    booking={slot.value as Booking}
                                />
                            ) : slot.type === "blocked" ? (
                                <BlockedSlotItem
                                    slot={slot.value as BlockedTimeSlot}
                                    onUnblockSlot={handleUnblockSlot}
                                    onRequestMakeRecurring={
                                        handleMakeRecurring
                                    }
                                    onEditSlot={handleEditSlot}
                                />
                            ) : (
                                <FreeSlotItem
                                    uuid={uuid}
                                    brand_id={brand_id}
                                    slot={slot.value as FreeBookingSlot}
                                    isNonWorkingDay={
                                        week.find(
                                            (d) =>
                                                d.day.format("YYYY-MM-DD") ===
                                                (
                                                    slot.value as any
                                                ).start.format("YYYY-MM-DD")
                                        )?.summary?.count === -1
                                    }
                                    onBlockSlot={handleBlockSlot}
                                />
                            )}
                        </Box>
                    ))}
                </Stack>

                {/* All modals — unchanged */}
                <AlertModal
                    open={dayOffModalOpen}
                    onClose={() => setDayOffModalOpen(false)}
                    alertText={dayOffModalContent.text}
                    headingText={dayOffModalContent.header}
                    buttonText={dayOffModalContent.button}
                    icon={<WbSunnyRounded sx={{ fontSize: 24 }} />}
                    onAction={() =>
                        navigate(
                            `/aiadmin/free-my-day/clients-review?date=${selectedDay.format(
                                "YYYY-MM-DD"
                            )}`
                        )
                    }
                />
                <AlertModal
                    open={fillDayModalOpen}
                    onClose={() => setFillDayModalOpen(false)}
                    alertText={fillDayModalContent.text}
                    headingText={fillDayModalContent.header}
                    buttonText={fillDayModalContent.button}
                    icon={<AutoFixHighRounded sx={{ fontSize: 24 }} />}
                    onAction={handleFillMyDayClick}
                />
                <AlertModal
                    open={freedModalOpen}
                    onClose={() => setFreedModalOpen(false)}
                    headingText="Enjoy your day off"
                    alertText="All clients were notified and the day was successfully blocked."
                    buttonText="Got it!"
                />
                <AlertModal
                    open={filledModalOpen}
                    onClose={() => setFilledModalOpen(false)}
                    headingText="Promotion launched!"
                    alertText="Your clients will receive an offer to book remaining slots today."
                    buttonText="Nice"
                />
                <AlertModal
                    open={jobExistsModalOpen}
                    onClose={() => setJobExistsModalOpen(false)}
                    headingText="Already scheduled"
                    alertText="A promotion is already scheduled for this day."
                    buttonText="Got it"
                />
                <AlertModal
                    open={bookingDisabledModalOpen}
                    onClose={() => setBookingDisabledModalOpen(false)}
                    headingText="Booking is disabled"
                    alertText="Online booking is disabled. You can enable it in Automation Settings."
                    buttonText="Open settings"
                    onAction={() => {
                        setBookingDisabledModalOpen(false);
                        navigate("/menu/automations");
                    }}
                />
                <ErrorDialog
                    open={errorDialogOpen}
                    errorMessage={errorMessage ?? ""}
                    onClose={() => setErrorDialogOpen(false)}
                />
                <AlertModal
                    open={makeRecurringSuccessOpen}
                    onClose={() => setMakeRecurringSuccessOpen(false)}
                    headingText={
                        makeRecurringSuccessCount === 0
                            ? "No recurring slots created"
                            : "Recurring blocked slot created"
                    }
                    alertText={
                        makeRecurringSuccessCount === 0
                            ? "All upcoming weeks already have appointments or blocked slots at this time. No new recurring slots were created."
                            : makeRecurringConflictCount > 0
                            ? `Recurring slot was created, but ${makeRecurringConflictCount} conflicting weeks were skipped.`
                            : "Recurring slot was created successfully!"
                    }
                    buttonText={
                        makeRecurringSuccessCount === 0 ? "I see" : "Great"
                    }
                />
                <ErrorDialog
                    open={!!makeRecurringError}
                    errorMessage={makeRecurringError || ""}
                    onClose={() => setMakeRecurringError(null)}
                />
            </Stack>
        </Stack>
    );
};

export default WeekView;
